import { Box, CircularProgress, Pagination } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ListingCard } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { decodeLatLngBounds } from '../../util/urlHelpers';
import { dedupeAndRankListings, isVisibleListing } from '../../util/listingFiltering';
import { fetchRentalTideListings } from '../../util/rentaltideService';
import { queryListingsForCity } from '../DynamicPage/DynamicPage.duck';

const decodeBoundsProp = boundsProp => {
  if (!boundsProp) return null;
  if (typeof boundsProp !== 'string') return boundsProp;
  const decoded = decodeLatLngBounds(boundsProp);
  if (!decoded) {
    console.warn('[CitySearchListings] could not decode bounds string:', boundsProp);
  }
  return decoded;
};

/**
 * Renders the same set of listings that /s would return for the given bounds.
 *
 * Pulls from BOTH sources that the search page uses:
 *   - Sharetribe (sdk.listings.query)
 *   - RentalTide partner inventory (/api/rentaltide/listings)
 *
 * Stateless: each mount fetches its own results, so navigating between city
 * pages can never show stale data from a previously visited city.
 */
export default function CitySearchListings({ bounds: boundsProp, perPage = 24 }) {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const [sharetribeListings, setSharetribeListings] = useState([]);
  const [rentalTideListings, setRentalTideListings] = useState([]);
  const [sharetribeMeta, setSharetribeMeta] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const decoded = decodeBoundsProp(boundsProp);
    if (!decoded) {
      setSharetribeListings([]);
      setRentalTideListings([]);
      setSharetribeMeta({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    const sharetribePromise = dispatch(
      queryListingsForCity({ bounds: decoded, page, perPage }, config)
    ).catch(e => {
      console.warn('[CitySearchListings] Sharetribe query failed:', e);
      return { listings: [], meta: {} };
    });

    // Only fetch RentalTide on page 1 — it isn't paginated alongside Sharetribe.
    const rentalTidePromise =
      page === 1
        ? fetchRentalTideListings({ bounds: decoded, perPage: 100 }).catch(e => {
            console.warn('[CitySearchListings] RentalTide query failed:', e);
            return { data: [] };
          })
        : Promise.resolve({ data: [] });

    Promise.all([sharetribePromise, rentalTidePromise]).then(([st, rt]) => {
      if (cancelled) return;
      setSharetribeListings(st.listings || []);
      setSharetribeMeta(st.meta || {});
      setRentalTideListings(rt.data || []);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [boundsProp, page, perPage, dispatch, config]);

  const displayListings = useMemo(() => {
    // Merge: RentalTide first so partner tier resolution is stable, but
    // dedupeAndRankListings will tier-rank everything together anyway.
    const merged = [...rentalTideListings, ...sharetribeListings];
    const visible = merged.filter(isVisibleListing);
    const filtered = visible.filter(l => l?.attributes?.title !== 'Location variant');
    return dedupeAndRankListings(filtered);
  }, [sharetribeListings, rentalTideListings]);

  if (loading && displayListings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!loading && displayListings.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap="2rem">
        {displayListings.map(listing => (
          <Box
            sx={{
              width: {
                xs: '100%',
                sm: `calc((100% / 2) - 1rem)`,
                md: `calc((100% / 3) - 1.34rem)`,
              },
              opacity: loading ? 0.5 : 1,
            }}
            key={listing.id.uuid}
          >
            <ListingCard listing={listing} />
          </Box>
        ))}
      </Box>
      {sharetribeMeta?.totalPages > 1 ? (
        <Box display="flex" justifyContent="center" paddingTop="3rem">
          <Pagination
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            count={sharetribeMeta.totalPages}
          />
        </Box>
      ) : null}
    </Box>
  );
}
